import type { OidcRpLoginOptions } from '@idport/oidc-rp-sdk';

import { logger } from '@luna/logger';
import { getEnv } from '@luna/env';
import type { RetailEnvironmentConfig } from '@retail/core-config';
import type { AuthConfig } from '@luna/oidc';
import {
    getAuthData,
    getBacklinkPathFromUrl,
    loadTokenWithCodeWorkflow,
    redirectToOidcLoginPage,
    saveLocalhostCountry,
    setAuthData,
} from '@luna/oidc';
import { setRetailToken } from '@retail/core';

import { getRpSdk } from './rpSdk';
import { logoutWithOIDC } from './OidcLogoutService';

interface OidcParams {
    code: string;
    idToken: string;
    state: string;
}

const OIDC_LOGIN_STATE = 'login';

export function getSearchParams(): URLSearchParams {
    // params can be after hash
    return new URLSearchParams(
        `${window.location.search.substring(1)}&${window.location.hash.substring(1)}`,
    );
}

function joinUrlAndPathname(url: string, pathname: string): string {
    if (url?.endsWith('/')) {
        return `${url}${pathname.substring(1)}`;
    }
    return `${url}${pathname}`;
}

function getEnvWithRedirectUriWithPath(envConfig: RetailEnvironmentConfig): AuthConfig {
    const countryParam =
        window.location.hostname === 'localhost' ? `?country=${getEnv().country}` : '';
    if (!envConfig.auth) {
        throw new Error('Missing oidc env configuration');
    }
    return {
        ...envConfig,
        auth: {
            ...envConfig.auth,
            redirectUri: `${joinUrlAndPathname(envConfig.auth.redirectUri, window.location.pathname)}${countryParam}`,
        },
    };
}

const getOidcParams = (): OidcParams => {
    // params can be after hash
    const searchParams = getSearchParams();
    const code = searchParams.get('code') ?? '';
    const idToken = searchParams.get('id_token') ?? '';
    const state = searchParams.get('state') ?? '';
    return { code, idToken, state };
};

export const isRedirectFromOidcLogin = (): boolean => {
    const { code, idToken, state } = getOidcParams();
    return !!code && !!idToken && state === OIDC_LOGIN_STATE;
};

const saveOidcDeepLinkOnUnload = (): void => {
    const { href, hash } = window.location;
    if (!hash || isRedirectFromOidcLogin()) {
        return;
    }

    sessionStorage.setItem('redirect', href);
};

function getLoginOptions(): OidcRpLoginOptions {
    const config = getEnv<RetailEnvironmentConfig>();
    return {
        redirectUri: joinUrlAndPathname(config.rpRedirectUri, window.location.pathname),
        scope: config.rpScopes,
        state: 'login',
        loginHint: getSearchParams().get('login_hint') ?? undefined,
    };
}

function getIdTokenHint(): string | undefined {
    return getSearchParams().get('id_token_hint') ?? undefined;
}

export const generateOidcAuthenticationRequest = (): Promise<void> => {
    saveOidcDeepLinkOnUnload();
    if (getEnv().country === 'CZ') {
        const rpSdk = getRpSdk();
        saveLocalhostCountry();
        return rpSdk
            .loginWithRedirect(getLoginOptions())
            .catch((e) => logger.error('redirect to login error', e));
    }
    const envConfig = getEnv<RetailEnvironmentConfig>();
    if (!envConfig.auth) {
        throw new Error(
            'Oidc should be used, but env config has no oidc config, check auth property',
        );
    }
    return redirectToOidcLoginPage(
        getEnvWithRedirectUriWithPath(envConfig),
        getBacklinkPathFromUrl(),
        'login',
        'GET',
        getIdTokenHint(),
    );
};

const getRedirectUrl = (): string => {
    const countryParam =
        window.location.hostname === 'localhost' ? `country=${getEnv().country}` : '';
    return (
        window.sessionStorage.getItem('redirect') ??
        `${window.location.pathname}?${countryParam}#overview`
    );
};

const SUCCESS_TYPE = 'success';

function generateOidcAccessTokenWithRpSdk(): Promise<void> {
    const { rpRedirectUri } = getEnv<RetailEnvironmentConfig>();
    const rpSdk = getRpSdk();
    return rpSdk
        .handleLoginWithRedirectCallback(
            joinUrlAndPathname(rpRedirectUri, window.location.pathname) + window.location.hash,
        )
        .then((result) => {
            if (result.type === SUCCESS_TYPE && result.accessToken) {
                setRetailToken(result.accessToken);
                setAuthData({
                    idToken: result.idToken?.raw,
                });
                window.history.replaceState({}, '', getRedirectUrl());
                return;
            }
            logger.error('login returned error', result);
        })
        .catch((e) => {
            logger.error('login failed', e);
            return logoutWithOIDC();
        })
        .finally(() => {
            sessionStorage.removeItem('redirect');
            sessionStorage.removeItem('country');
        });
}

async function generateOidcAccessTokenWithLunaOidc(): Promise<void> {
    const envConfig = getEnv<RetailEnvironmentConfig>();
    await loadTokenWithCodeWorkflow(getEnvWithRedirectUriWithPath(envConfig));
    setRetailToken(getAuthData().token!);
    window.history.replaceState({}, '', getRedirectUrl());
    sessionStorage.removeItem('redirect');
    sessionStorage.removeItem('country');
}

export const generateOidcAccessToken = async (): Promise<void> => {
    if (getEnv().country === 'CZ') {
        return generateOidcAccessTokenWithRpSdk();
    }

    return generateOidcAccessTokenWithLunaOidc();
};
